import React, { useState, useRef } from 'react';

import { Camera, Upload, X, Check, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { ensureFaceApiLoaded, computeFaceDescriptorsFromBlob } from '../utils/face';

interface PhotoUploadProps {
  onUploadSuccess?: (photoData: any) => void;
  onClose?: () => void;
  eventType?: string;
}

interface UploadStatus {
  type: 'idle' | 'uploading' | 'success' | 'error';
  message?: string;
}

interface SelectedPhoto {
  file: File;
  previewUrl: string;
  id: string;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({ 
  onUploadSuccess,
  onClose,
  eventType = 'general'
}) => {
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ type: 'idle' });
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const newPhotos: SelectedPhoto[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large (max 10MB)`);
        return;
      }

      // Check if photo already selected
      const isDuplicate = selectedPhotos.some(photo => 
        photo.file.name === file.name && photo.file.size === file.size
      );
      
      if (!isDuplicate) {
        const previewUrl = URL.createObjectURL(file);
        newPhotos.push({
          file,
          previewUrl,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        });
      }
    });

    if (errors.length > 0) {
      setUploadStatus({
        type: 'error',
        message: errors.join(', ')
      });
    } else {
      setUploadStatus({ type: 'idle' });
    }

    if (newPhotos.length > 0) {
      setSelectedPhotos(prev => [...prev, ...newPhotos]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (selectedPhotos.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Please select at least one photo to upload'
      });
      return;
    }

    setUploadStatus({ type: 'uploading', message: `Uploading ${selectedPhotos.length} photo(s)...` });
    setUploadProgress(0);

    // Helper: compress images client-side to avoid 413 on serverless
    const compressImageIfNeeded = async (file: File): Promise<File> => {
      const isCompressible = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
      const isHEIC = file.type === 'image/heic' || file.type === 'image/heif';
      // Skip compression for HEIC/HEIF (browser decoding is unreliable)
      if (!isCompressible || isHEIC) return file;

      // Only compress if larger than ~4MB
      const sizeLimit = 4 * 1024 * 1024;
      if (file.size <= sizeLimit) return file;

      const objectUrl = URL.createObjectURL(file);
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = objectUrl;
        });

        const maxDim = 2000; // max width/height
        let { width, height } = img;
        if (width > height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height >= width && height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, width);
        canvas.height = Math.max(1, height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Try a few quality levels to get under the size limit
        const qualities = [0.8, 0.7, 0.6, 0.5];
        for (const q of qualities) {
          const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', q));
          if (!blob) continue;
          if (blob.size <= sizeLimit || q === qualities[qualities.length - 1]) {
            return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          }
        }
        return file;
      } catch {
        return file;
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    try {
      const results: Array<{ success: boolean }> = [];
      try {
        await ensureFaceApiLoaded('https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/');
      } catch {
        // Uploads still work if face models are unavailable.
      }

      for (let index = 0; index < selectedPhotos.length; index++) {
        const photo = selectedPhotos[index]!;
        const processedFile = await compressImageIfNeeded(photo.file);

        let result: any = { success: false };
        try {
          const formData = new FormData();
          formData.append('photo', processedFile);
          formData.append('guestName', 'Wedding Guest');
          formData.append('eventType', eventType);

          try {
            const { descriptors, boxes } = await computeFaceDescriptorsFromBlob(processedFile);
            if (descriptors.length > 0) {
              const limitedDescriptors = descriptors.slice(0, 10);
              formData.append('faceData', JSON.stringify({
                descriptors: limitedDescriptors,
                boxes: boxes.slice(0, limitedDescriptors.length),
              }));
            }
          } catch {
            // Face indexing is optional and must never block an upload.
          }

          const response = await fetch('/api/photos', { method: 'POST', body: formData });
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || `Upload failed (${response.status})`);
          }
          result = await response.json();
        } catch (error: any) {
          setUploadStatus({ type: 'error', message: error?.message || 'Upload failed' });
        }

        results.push(result);
        setUploadProgress(((index + 1) / selectedPhotos.length) * 100);
      }

      const successCount = results.filter(result => result.success).length;
      const failCount = results.length - successCount;

      if (successCount === results.length) {
        setUploadStatus({
          type: 'success',
          message: `All ${successCount} photo(s) uploaded successfully! Thank you for sharing these moments.`
        });
      } else if (successCount > 0) {
        setUploadStatus({
          type: 'success',
          message: `${successCount} photo(s) uploaded successfully. ${failCount} failed to upload.`
        });
      } else {
        setUploadStatus({
          type: 'error',
          message: 'Failed to upload photos. Please try again.'
        });
      }

      if (onUploadSuccess && successCount > 0) {
        onUploadSuccess({ count: successCount });
      }

      // Reset form after successful upload
      setTimeout(() => {
        selectedPhotos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
        setSelectedPhotos([]);
        setUploadStatus({ type: 'idle' });
        setUploadProgress(0);
      }, 3000);
    } catch (error) {
      // Handle error silently
      setUploadStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    }
  };

  const removePhoto = (photoId: string) => {
    setSelectedPhotos(prev => {
      const photoToRemove = prev.find(photo => photo.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }
      return prev.filter(photo => photo.id !== photoId);
    });
  };

  const clearAllPhotos = () => {
    selectedPhotos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
    setSelectedPhotos([]);
    setUploadStatus({ type: 'idle' });
  };

  const getStatusIcon = () => {
    switch (uploadStatus.type) {
      case 'uploading':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rose-600"></div>;
      case 'success':
        return <Check className="h-5 w-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (uploadStatus.type) {
      case 'success':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'uploading':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return '';
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {onClose && (
          <div className="flex justify-between items-center p-4 border-b border-gray-100">
            <h2 className="text-xl font-serif text-sage-800">Share Photos</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {!onClose && (
          <div className="text-center p-6 border-b border-gray-100">
            <Camera className="h-10 w-10 text-sage-600 mx-auto mb-3" />
            <h2 className="text-xl font-serif text-sage-800 mb-2">Share Wedding Photos</h2>
            <p className="text-gray-600 text-sm">Help us capture every beautiful moment of this special day</p>
          </div>
        )}

        <div className="p-4 sm:p-6">
          {/* File Upload Area */}
          <div className="space-y-4">
            {/* Add Photos Button */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-sage-400 transition-colors touch-manipulation"
            >
              <Plus className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">Add Photos</p>
              <p className="text-sm text-gray-400 mt-1">Select multiple photos • JPEG, PNG, WebP • Max 10MB each</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,image/heic,image/heif"
              onChange={handleFileSelect}
              className="hidden"
              multiple
              disabled={uploadStatus.type === 'uploading'}
            />

            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="inline-flex items-center space-x-2 text-sage-700 hover:text-sage-900 text-sm font-medium px-3 py-2"
                disabled={uploadStatus.type === 'uploading'}
              >
                <Camera className="h-4 w-4" />
                <span>Use Camera</span>
              </button>
            </div>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploadStatus.type === 'uploading'}
            />

            {/* Selected Photos Grid */}
            {selectedPhotos.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Selected Photos ({selectedPhotos.length})
                  </h3>
                  {selectedPhotos.length > 1 && (
                    <button
                      onClick={clearAllPhotos}
                      className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center space-x-1 p-2"
                      disabled={uploadStatus.type === 'uploading'}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Clear All</span>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {selectedPhotos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.previewUrl}
                        alt="Preview"
                        className="w-full h-24 sm:h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(photo.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 touch-manipulation"
                        disabled={uploadStatus.type === 'uploading'}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploadStatus.type === 'uploading' && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading photos...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-sage-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Status Message */}
            {uploadStatus.message && (
              <div className={`p-3 rounded-lg border flex items-center space-x-2 ${getStatusColor()}`}>
                {getStatusIcon()}
                <span className="text-sm flex-1">{uploadStatus.message}</span>
              </div>
            )}

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={selectedPhotos.length === 0 || uploadStatus.type === 'uploading'}
              className="w-full bg-sage-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-sage-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 touch-manipulation"
            >
              {uploadStatus.type === 'uploading' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>
                    Upload {selectedPhotos.length > 0 ? `${selectedPhotos.length} Photo${selectedPhotos.length > 1 ? 's' : ''}` : 'Photos'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
